'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Cross } from '@/components/site/Cross'

/**
 * The two vertical rules that run the length of the page, and the small
 * crosses where a section's rule meets them.
 *
 * The design draws this rather than styling it, and it has to: the rules sit
 * on the content band's rail, the crosses land on section boundaries that only
 * exist once the page has laid out, and a full-bleed block (the stack marquee)
 * breaks the rules rather than crossing them. None of that is expressible as a
 * border.
 *
 * Measured from the DOM on mount, on resize and on scroll — the last because a
 * page grows as images load and sections reveal, and the boundaries move with
 * it. The measurement is keyed, so a scroll that changes nothing renders
 * nothing.
 */
type Grid = { xs: number[]; segments: [number, number][]; ys: number[]; height: number }

const BAND = 1320
const RAIL = 14

export function RailGrid() {
    const host = useRef<HTMLDivElement>(null)
    const key = useRef('')
    const [grid, setGrid] = useState<Grid | null>(null)

    const measure = useCallback(() => {
        const element = host.current
        const shell = element?.parentElement

        if (element == null || shell == null) {
            return
        }

        const bounds = shell.getBoundingClientRect()
        const main = document.querySelector('main')

        // The rails stop where the footer begins: the footer is sticky and
        // carries its own rules, and running these behind it doubles them.
        const height = Math.round(
            main === null ? bounds.height : main.getBoundingClientRect().bottom - bounds.top,
        )

        const rail = Math.max(0, (bounds.width - BAND) / 2) + RAIL
        const xs = [rail, Math.round(bounds.width - rail)]

        // A boundary is where a section starts or ends. The header's own rule
        // is not one of them: it is sticky, so it holds one place on the screen
        // while these crosses travel with the page. They pass behind it, which
        // is what the header's translucency is for.
        const ys = new Set<number>()

        for (const section of document.querySelectorAll('main section')) {
            const rect = section.getBoundingClientRect()

            ys.add(Math.round(rect.top - bounds.top))
            ys.add(Math.round(rect.bottom - bounds.top))
        }

        // Blocks that run the full width of the window break the rails rather
        // than being crossed by them.
        const gaps: [number, number][] = []

        for (const gap of document.querySelectorAll('[data-no-rails]')) {
            const rect = gap.getBoundingClientRect()

            gaps.push([Math.round(rect.top - bounds.top), Math.round(rect.bottom - bounds.top)])
        }

        gaps.sort((a, b) => a[0] - b[0])

        const segments: [number, number][] = []
        let cursor = 0

        for (const [top, bottom] of gaps) {
            if (top > cursor) {
                segments.push([cursor, top])
            }

            cursor = Math.max(cursor, bottom)
        }

        segments.push([cursor, height])

        const measured: Grid = {
            xs,
            segments: segments.filter(([top, bottom]) => bottom - top >= 8),
            ys: [...ys].filter((y) => y <= height + 2).sort((a, b) => a - b),
            height,
        }

        const next = JSON.stringify(measured)

        if (next === key.current) {
            return
        }

        key.current = next
        setGrid(measured)
    }, [])

    useEffect(() => {
        let ticking = false

        const onScroll = () => {
            if (ticking) {
                return
            }

            ticking = true

            requestAnimationFrame(() => {
                ticking = false
                measure()
            })
        }

        measure()

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        // Fonts land after first paint and move every boundary below them.
        document.fonts?.ready.then(measure).catch(() => {})

        const observer = new ResizeObserver(onScroll)
        const shell = host.current?.parentElement

        if (shell != null) {
            observer.observe(shell)
        }

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            observer.disconnect()
        }
    }, [measure])

    return (
        <>
            {/* The rails run above the header: they are the page's own edges
             * and they run its whole height, and stopping them at the bar
             * leaves it floating between two stubs. */}
            <div
                ref={host}
                data-rails
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-85"
            >
                {grid === null
                    ? null
                    : grid.xs.map((x) =>
                          grid.segments.map(([top, bottom]) => (
                              <span
                                  key={`${x}-${top}`}
                                  className="absolute w-px bg-hair"
                                  style={{ left: x, top, height: bottom - top }}
                              />
                          )),
                      )}
            </div>

            {/* The crosses stay under it. A section boundary travels with the
             * page and passes through the header on its way out, and a cross
             * drawn over the bar as it goes is the thing that read as broken. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-2">
                {grid === null
                    ? null
                    : grid.xs.map((x) =>
                          grid.ys.map((y) => <Cross key={`${x}-${y}`} style={{ left: x, top: y }} />),
                      )}
            </div>
        </>
    )
}
